import React, { useMemo } from 'react';
import { ATL_SKYLINE } from './skylineData';

// Layer → CSS class map (from the Corel export):
//   fil0 = dome disc   fil1 = building silhouette   fil2 = spire/antenna
//   fil3 = window & mullion lines (str0/str1)        fil4 = crowd silhouette
function skylinePalette(tokens) {
    if (tokens.mode === 'dark') {
        return {
            dome: '#2c3a6b', domeOpacity: 0.6,
            build: '#3f4a64',
            spire: '#525d78',
            win: '#ffd29a', winWidth: 7, winOpacity: 0.62,
            mullion: '#9fb0d8', mullionWidth: 5, mullionOpacity: 0.28,
            people: '#0e131c',
            svgOpacity: 0.72,
        };
    }
    return {
        dome: '#f0d2c0', domeOpacity: 0.85,
        build: '#9aa3b4',
        spire: '#8a93a4',
        win: '#ffffff', winWidth: 6, winOpacity: 0.45,
        mullion: '#6f7889', mullionWidth: 4, mullionOpacity: 0.35,
        people: '#5d6678',
        svgOpacity: 0.66,
    };
}

let _seq = 0;

// Shared calendar watermark sizing — month and week use the same values so the
// skyline spans the same width in both views (width-driven, not height-driven).
export const SKYLINE_WATERMARK_CONTAINER_HEIGHT = '46%';
export const SKYLINE_WATERMARK_WIDTH = '72%';
export const SKYLINE_WATERMARK_MAX_WIDTH = 1200;

// containerHeight, svgWidth, svgMaxWidth allow callers to tune size for different
// contexts (calendar grid vs sub-page full-bleed).
const SkylineWatermark = ({
    tokens,
    containerHeight = SKYLINE_WATERMARK_CONTAINER_HEIGHT,
    svgWidth = SKYLINE_WATERMARK_WIDTH,
    svgMaxWidth = SKYLINE_WATERMARK_MAX_WIDTH,
}) => {
    const uid = useMemo(() => `sky-${++_seq}`, []);
    const dark = tokens.mode === 'dark';
    const p = skylinePalette(tokens);

    const glow = dark
        ? 'linear-gradient(to top, rgba(130,150,190,0.13), transparent 80%)'
        : 'linear-gradient(to top, rgba(92,112,145,0.17), transparent 80%)';

    const css = `
        #${uid} .fil0 { fill: ${p.dome}; opacity: ${p.domeOpacity}; }
        #${uid} .fil1 { fill: ${p.build}; }
        #${uid} .fil2 { fill: ${p.spire}; }
        #${uid} .fil3 { fill: none; }
        #${uid} .fil4 { fill: ${p.people}; }
        #${uid} .str0 { stroke: ${p.win}; stroke-width: ${p.winWidth}; stroke-opacity: ${p.winOpacity}; }
        #${uid} .str1 { stroke: ${p.mullion}; stroke-width: ${p.mullionWidth}; stroke-opacity: ${p.mullionOpacity}; }
        #${uid} .fil2.str0 { stroke: ${p.spire}; stroke-opacity: 1; }
    `;

    return (
        <div
            aria-hidden="true"
            style={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                height: containerHeight, zIndex: 0, pointerEvents: 'none', overflow: 'visible',
            }}
        >
            <div style={{ position: 'absolute', inset: 0, background: glow, overflow: 'hidden' }} />
            <svg
                id={uid}
                viewBox={ATL_SKYLINE.viewBox}
                preserveAspectRatio="xMidYMax meet"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                style={{
                    position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                    width: svgWidth, maxWidth: svgMaxWidth, height: 'auto',
                    aspectRatio: '7227.11 / 4006.07',
                    opacity: p.svgOpacity, userSelect: 'none',
                    WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)',
                    maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 100%)',
                }}
            >
                {/* eslint-disable-next-line react/no-danger */}
                <style dangerouslySetInnerHTML={{ __html: css }} />
                {/* eslint-disable-next-line react/no-danger */}
                <g dangerouslySetInnerHTML={{ __html: ATL_SKYLINE.markup }} />
            </svg>
        </div>
    );
};

export default SkylineWatermark;
